/**
 * Resend Test Page
 * 
 * This page allows testing the new email system in production.
 * It sends test emails using the shared email client.
 */

import { useState } from 'react';
import { createEmailClient } from '@masonic-tools/shared-email';
import { auth } from '../lib/firebase';
import Swal from 'sweetalert2';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Spinner } from '../components/ui/spinner';
import Header from '../components/layout/Header';

// Create email client
const emailClient = createEmailClient({
    apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000',
    getAuthToken: async () => {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('User not authenticated');
        }
        return user.getIdToken();
    }
});

interface EmailLog {
    timestamp: Date;
    to: string;
    subject: string;
    status: 'sending' | 'success' | 'error';
    emailId?: string;
    error?: string;
}

export default function ResendTestPage() {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        to: 'luke@masonic-tools.co.uk',
        subject: 'Test Email from Admin Panel',
        message: 'This is a test email sent from the Masonic Tools Admin Panel using the new email system.',
    });
    const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addLog = (log: Omit<EmailLog, 'timestamp'>) => {
        setEmailLogs(prev => [{
            ...log,
            timestamp: new Date()
        }, ...prev]);
    };

    const sendTestEmail = async () => {
        if (!formData.to || !formData.subject || !formData.message) {
            await Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all fields'
            });
            return;
        }

        setLoading(true);
        addLog({
            to: formData.to,
            subject: formData.subject,
            status: 'sending'
        });

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                throw new Error('Not authenticated');
            }

            // Generate HTML email
            const html = `
<!DOCTYPE html>
<html>
<head>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
        }
        .header {
            background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
            color: white;
            padding: 30px;
            border-radius: 10px 10px 0 0;
            text-align: center;
        }
        .header h1 {
            margin: 0;
            font-size: 24px;
            font-weight: 700;
        }
        .content {
            background: #f9fafb;
            padding: 30px;
            border-radius: 0 0 10px 10px;
            border: 1px solid #e5e7eb;
            border-top: none;
        }
        .message-box {
            background: white;
            padding: 20px;
            border-radius: 6px;
            border: 1px solid #e5e7eb;
            margin: 20px 0;
        }
        .info {
            background: #dbeafe;
            padding: 15px;
            border-radius: 6px;
            margin: 20px 0;
            border-left: 4px solid #3b82f6;
        }
        .footer {
            text-align: center;
            padding: 20px;
            color: #6b7280;
            font-size: 12px;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Test Email from Admin Panel</h1>
    </div>
    <div class="content">
        <div class="info">
            <strong>This is a test email</strong><br>
            Sent from: Masonic Tools Admin Panel<br>
            Environment: ${import.meta.env.MODE || 'development'}<br>
            API URL: ${import.meta.env.VITE_API_URL || 'localhost'}<br>
            Sent by: ${currentUser.email}
        </div>
        
        <div class="message-box">
            ${formData.message.replace(/\n/g, '<br>')}
        </div>
        
        <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
            Timestamp: ${new Date().toLocaleString('en-GB', {
                dateStyle: 'full',
                timeStyle: 'long',
                timeZone: 'Europe/London'
            })}
        </p>
    </div>
    <div class="footer">
        <p>Masonic Tools Email System</p>
        <p>masonic-tools.co.uk</p>
    </div>
</body>
</html>
            `.trim();

            const text = `
Test Email from Admin Panel

${formData.message}

---
Sent from: Masonic Tools Admin Panel
Environment: ${import.meta.env.MODE || 'development'}
API URL: ${import.meta.env.VITE_API_URL || 'localhost'}
Sent by: ${currentUser.email}
Timestamp: ${new Date().toLocaleString('en-GB')}

Masonic Tools Email System
masonic-tools.co.uk
            `.trim();

            // Send email
            const result = await emailClient.send({
                to: formData.to,
                subject: formData.subject,
                html,
                text,
                templateType: 'test',
                tags: [
                    { name: 'source', value: 'admin-panel' },
                    { name: 'type', value: 'test' },
                    { name: 'environment', value: import.meta.env.MODE || 'development' }
                ]
            });

            if (result.success) {
                // Update log with success
                setEmailLogs(prev => prev.map((log, idx) => 
                    idx === 0 
                        ? { ...log, status: 'success', emailId: result.emailId }
                        : log
                ));

                await Swal.fire({
                    icon: 'success',
                    title: 'Email Sent Successfully!',
                    html: `
                        <p>Email has been sent to <strong>${formData.to}</strong></p>
                        ${result.emailId ? `<p class="text-sm text-gray-600 mt-2">Email ID: ${result.emailId}</p>` : ''}
                    `,
                    confirmButtonText: 'Great!'
                });
            } else {
                throw new Error(result.error || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            
            // Update log with error
            setEmailLogs(prev => prev.map((log, idx) => 
                idx === 0 
                    ? { ...log, status: 'error', error: error instanceof Error ? error.message : String(error) }
                    : log
            ));

            await Swal.fire({
                icon: 'error',
                title: 'Failed to Send Email',
                text: error instanceof Error ? error.message : 'Unknown error occurred',
                footer: 'Check the console for more details'
            });
        } finally {
            setLoading(false);
        }
    };

    const sendQuickTest = async () => {
        const currentData = { ...formData };
        setFormData({
            to: 'luke@masonic-tools.co.uk',
            subject: `Quick Test - ${new Date().toLocaleTimeString()}`,
            message: `Quick test email sent at ${new Date().toLocaleString()}\n\nEmail system is working correctly!`
        });
        
        // Wait a bit for state to update, then send
        setTimeout(() => {
            sendTestEmail();
            // Restore original data after send
            setTimeout(() => setFormData(currentData), 1000);
        }, 100);
    };

    const clearLogs = () => {
        setEmailLogs([]);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Resend Email Test</h1>
                    <p className="mt-2 text-gray-600">
                        Test the new email system in production. This uses the shared email client to send emails via the API service.
                    </p>
                    <div className="mt-4 flex items-center gap-2 text-sm">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Environment: {import.meta.env.MODE || 'development'}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            API: {import.meta.env.VITE_API_URL || 'localhost:3000'}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Email Form */}
                    <Card className="p-6">
                        <h2 className="text-xl font-semibold mb-4">Send Test Email</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="to">Recipient Email</Label>
                                <Input
                                    id="to"
                                    name="to"
                                    type="email"
                                    value={formData.to}
                                    onChange={handleInputChange}
                                    placeholder="recipient@example.com"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <Label htmlFor="subject">Subject</Label>
                                <Input
                                    id="subject"
                                    name="subject"
                                    type="text"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    placeholder="Email subject"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <Label htmlFor="message">Message</Label>
                                <Textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Email message content"
                                    rows={6}
                                    disabled={loading}
                                />
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    onClick={sendTestEmail}
                                    disabled={loading}
                                    className="flex-1"
                                >
                                    {loading ? (
                                        <>
                                            <Spinner className="mr-2" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Send Test Email'
                                    )}
                                </Button>
                                
                                <Button
                                    onClick={sendQuickTest}
                                    disabled={loading}
                                    variant="outline"
                                >
                                    Quick Test
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Email Logs */}
                    <Card className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Email Log</h2>
                            {emailLogs.length > 0 && (
                                <Button
                                    onClick={clearLogs}
                                    variant="outline"
                                    size="sm"
                                >
                                    Clear
                                </Button>
                            )}
                        </div>

                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                            {emailLogs.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">
                                    No emails sent yet. Send a test email to see it appear here.
                                </p>
                            ) : (
                                emailLogs.map((log, idx) => (
                                    <div
                                        key={idx}
                                        className={`p-4 rounded-lg border ${
                                            log.status === 'success'
                                                ? 'bg-green-50 border-green-200'
                                                : log.status === 'error'
                                                ? 'bg-red-50 border-red-200'
                                                : 'bg-blue-50 border-blue-200'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                                        log.status === 'success'
                                                            ? 'bg-green-100 text-green-800'
                                                            : log.status === 'error'
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                        {log.status === 'success' && '✓ Sent'}
                                                        {log.status === 'error' && '✗ Failed'}
                                                        {log.status === 'sending' && '⏳ Sending'}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {log.timestamp.toLocaleTimeString()}
                                                    </span>
                                                </div>
                                                <p className="mt-2 text-sm font-medium text-gray-900">
                                                    To: {log.to}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {log.subject}
                                                </p>
                                                {log.emailId && (
                                                    <p className="mt-1 text-xs text-gray-500 font-mono">
                                                        ID: {log.emailId}
                                                    </p>
                                                )}
                                                {log.error && (
                                                    <p className="mt-2 text-xs text-red-600">
                                                        Error: {log.error}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </Card>
                </div>

                {/* Info Section */}
                <Card className="mt-6 p-6 bg-blue-50 border-blue-200">
                    <h3 className="text-lg font-semibold mb-3 text-blue-900">About This Test Page</h3>
                    <div className="space-y-2 text-sm text-blue-800">
                        <p>
                            This page uses the new <code className="px-1 py-0.5 bg-blue-100 rounded">@masonic-tools/shared-email</code> package
                            to send emails via the API service.
                        </p>
                        <p>
                            <strong>How it works:</strong>
                        </p>
                        <ol className="list-decimal list-inside space-y-1 ml-4">
                            <li>Gets your Firebase authentication token</li>
                            <li>Sends email request to the API service</li>
                            <li>API validates your token</li>
                            <li>API retrieves Resend API key from Secret Manager</li>
                            <li>API sends email via Resend</li>
                            <li>Returns success/failure response</li>
                        </ol>
                        <p className="mt-4">
                            <strong>Check emails in Resend Dashboard:</strong>{' '}
                            <a
                                href="https://resend.com/emails"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                https://resend.com/emails
                            </a>
                        </p>
                    </div>
                </Card>
            </main>
        </div>
    );
}

